// ADR 10.9.26j §3/§4 — one browser-owned box; wasm owns every transition.
//
// ADR 25.9.26f §3 — this file is the player's wasm-free **hub**: the store
// connection, the listeners, the playback generation, `publish`, `cancel`, the
// provider and the snapshot hook. `main.tsx` mounts the provider, so this
// module is on the boot graph and imports the facade for types only. Every
// transition wasm decides lives in the **driver**, `speechPlayer.ts`, which
// only `SpeakButton` (a wasm-side component) imports. The driver is not
// loaded on first `play()`: a promise hop between the tap and
// `speechSynthesis.speak` risks Safari's transient-activation gating.

import type {
  MessageRange, SpeechPace, SpeechSegment, SpeechWord, TutorPlaybackState,
} from '../../../wasm'
import {
  createContext,
  useContext,
  useLayoutEffect,
  useReducer,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import type { TtsSurface } from './ttsTelemetry'

export interface PlayerSnapshot {
  owner: string | null
  state: TutorPlaybackState
  highlight: MessageRange | null
  activeIndex: number
  primed: boolean
  /**
   * ADR 30.9.26j §2.5 — the full-segment UTF-16 offset of the last boundary
   * (or the slice base, or 0). With `activeIndex`, the voice's position.
   */
  spokenOffset: number
  /** ADR 30.9.26j §4.3 — seeded per request, kept across its seeks; `null` idle. */
  pace: SpeechPace | null
}

export interface PlayRequest {
  owner: string
  /** ADR 29.9.26i §1 — the app code, sent as `lang_code`; never a voice tag. */
  language: string
  /** ADR 29.9.26i §1 — `null` is "not counted", and must be said. */
  surface: TtsSurface | null
  messageText: string
  segments: SpeechSegment[]
  voices: SpeechSynthesisVoice[]
  rate?: number
  /** ADR 30.9.26j §3 — a precomputed word map; absent, the driver builds it lazily. */
  words?: SpeechWord[]
}

const IDLE: PlayerSnapshot = {
  owner: null,
  state: 'idle',
  highlight: null,
  activeIndex: 0,
  primed: false,
  spokenOffset: 0,
  pace: null,
}

type PlayerAction = { type: 'store'; snapshot: PlayerSnapshot }

/** Storage only: wasm has already decided every state carried by this action. */
export function playerReducer(_: PlayerSnapshot, action: PlayerAction): PlayerSnapshot {
  return action.snapshot
}

interface PlayerStore {
  getSnapshot(): PlayerSnapshot
  dispatch(action: PlayerAction): void
}

function localStore(): PlayerStore {
  let snapshot = IDLE
  return {
    getSnapshot: () => snapshot,
    dispatch: (action) => { snapshot = playerReducer(snapshot, action) },
  }
}

/** The state both halves share; the driver's transitions all publish through it. */
export class PlayerHub {
  private store = localStore()
  private listeners = new Set<() => void>()
  /** What is playing now: set by the driver, cleared by `cancel`. */
  request: PlayRequest | null = null
  /** Bumped by every transition that must silence a stale utterance's callbacks. */
  playbackGeneration = 0

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  getSnapshot = () => this.store.getSnapshot()

  connect(store: PlayerStore) {
    this.store = store
  }

  disconnect(store: PlayerStore) {
    if (this.store === store) this.store = localStore()
  }

  publish(next: PlayerSnapshot) {
    this.store.dispatch({ type: 'store', snapshot: next })
    this.listeners.forEach((listener) => listener())
  }

  cancel(owner?: string) {
    if (owner !== undefined && this.getSnapshot().owner !== owner) return
    ++this.playbackGeneration
    window.speechSynthesis?.cancel()
    this.request = null
    this.publish(IDLE)
  }
}

export const playerHub = new PlayerHub()

const PlayerContext = createContext<PlayerSnapshot | null>(null)

/** One reducer for the one module player; surfaces only subscribe to its value. */
export function SpeechPlayerProvider({ children }: { children: ReactNode }) {
  const [snapshot, dispatch] = useReducer(playerReducer, IDLE)
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const store = useRef<PlayerStore>({
    getSnapshot: () => snapshotRef.current,
    dispatch: (action) => {
      snapshotRef.current = playerReducer(snapshotRef.current, action)
      dispatch(action)
    },
  })
  useLayoutEffect(() => {
    const connected = store.current
    playerHub.connect(connected)
    return () => {
      playerHub.cancel()
      playerHub.disconnect(connected)
    }
  }, [])
  return <PlayerContext.Provider value={snapshot}>{children}</PlayerContext.Provider>
}

export function useSpeechPlayerSnapshot(): PlayerSnapshot {
  const context = useContext(PlayerContext)
  const external = useSyncExternalStore(playerHub.subscribe, playerHub.getSnapshot)
  return context ?? external
}
